import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getVisitors,
  getVisitorById,
  createVisitor,
  searchVisitors,
  getVisitorHistory,
  getVisits,
  getActiveVisits,
  getTodayVisits,
  checkIn,
  checkOut,
  getWatchListEntries,
  createWatchListEntry,
  updateWatchListEntry,
  deleteWatchListEntry,
  getReasonTypes,
  createReasonType,
  updateReasonType,
  deleteReasonType,
  getCategoryTypes,
  createCategoryType,
  updateCategoryType,
  deleteCategoryType,
  getBadgeTemplates,
  createBadgeTemplate,
  updateBadgeTemplate,
  deleteBadgeTemplate,
  getScreeningConfig,
  createScreeningConfig,
  updateScreeningConfig,
  getReasonTypeById,
  getCategoryTypeById,
  getBadgeTemplateById,
  getEmployeeReasonTypes,
  getEmployeeReasonTypeById,
  createEmployeeReasonType,
  updateEmployeeReasonType,
  deleteEmployeeReasonType,
  getEmployeeVisits,
  getActiveEmployeeVisits,
  getTodayEmployeeVisits,
  getEmployeeVisitById,
  createEmployeeVisit,
  updateEmployeeVisit,
  deleteEmployeeVisit,
  getCarRiderQueue,
  getCarRiderQueueEntryById,
  createCarRiderQueueEntry,
  updateCarRiderQueueEntry,
  deleteCarRiderQueueEntry,
  getPickupDelegations,
  getPickupDelegationById,
  createPickupDelegation,
  updatePickupDelegation,
  deletePickupDelegation,
} from '../../services/visitorManagement.service';
import type {
  VisitorFilters,
  VisitFilters,
  WatchListFilters,
  CheckInRequest,
  CheckOutRequest,
  VisitorCreate,
  WatchListEntryWrite,
  VisitReasonTypeWrite,
  VisitorCategoryTypeWrite,
  BadgeTemplateWrite,
  ScreeningConfigurationWrite,
  EmployeeReasonType,
  EmployeeReasonTypeWrite,
  EmployeeVisitListItem,
  EmployeeVisitDetail,
  EmployeeVisitCreate,
  EmployeeVisitUpdate,
  CarRiderQueueEntry,
  CarRiderQueueEntryWrite,
  PickupDelegation,
  PickupDelegationWrite,
} from '../../types/VisitorManagementTypes';

// =============================================================================
// Query Key Factories
// =============================================================================

export const vmKeys = {
  all: ['visitor-management'] as const,
  visitors: () => [...vmKeys.all, 'visitors'] as const,
  visitorList: (filters: VisitorFilters) =>
    [...vmKeys.visitors(), 'list', filters] as const,
  visitorDetail: (id: string) => [...vmKeys.visitors(), 'detail', id] as const,
  visitorSearch: (query: string) =>
    [...vmKeys.visitors(), 'search', query] as const,
  visitorHistory: (id: string) =>
    [...vmKeys.visitors(), 'history', id] as const,
  visits: () => [...vmKeys.all, 'visits'] as const,
  visitList: (filters: VisitFilters) =>
    [...vmKeys.visits(), 'list', filters] as const,
  activeVisits: (school?: number) =>
    [...vmKeys.visits(), 'active', school] as const,
  todayVisits: (school?: number) =>
    [...vmKeys.visits(), 'today', school] as const,
  watchList: () => [...vmKeys.all, 'watch-list'] as const,
  watchListEntries: (filters: WatchListFilters) =>
    [...vmKeys.watchList(), 'list', filters] as const,
  reasonTypes: () => [...vmKeys.all, 'reason-types'] as const,
  reasonTypeById: (id: string) => [...vmKeys.all, 'reason-types', id] as const,
  categoryTypes: () => [...vmKeys.all, 'category-types'] as const,
  categoryTypeById: (id: string) =>
    [...vmKeys.all, 'category-types', id] as const,
  badgeTemplates: () => [...vmKeys.all, 'badge-templates'] as const,
  badgeTemplateById: (id: string) =>
    [...vmKeys.all, 'badge-templates', id] as const,
  screeningConfig: () => [...vmKeys.all, 'screening-config'] as const,
  employeeReasonTypes: () => [...vmKeys.all, 'employee-reason-types'] as const,
  employeeReasonTypeById: (id: string) => [...vmKeys.all, 'employee-reason-types', id] as const,
  employeeVisits: () => [...vmKeys.all, 'employee-visits'] as const,
  employeeVisitList: (filters: Record<string, unknown>) => [...vmKeys.employeeVisits(), 'list', filters] as const,
  activeEmployeeVisits: (school?: number) => [...vmKeys.employeeVisits(), 'active', school] as const,
  todayEmployeeVisits: (school?: number) => [...vmKeys.employeeVisits(), 'today', school] as const,
  employeeVisitDetail: (id: string) => [...vmKeys.employeeVisits(), 'detail', id] as const,
  carRiderQueue: () => [...vmKeys.all, 'car-rider-queue'] as const,
  carRiderQueueEntryById: (id: string) => [...vmKeys.all, 'car-rider-queue', id] as const,
  pickupDelegations: () => [...vmKeys.all, 'pickup-delegations'] as const,
  pickupDelegationById: (id: string) => [...vmKeys.all, 'pickup-delegations', id] as const,
};

// =============================================================================
// Visitor Hooks
// =============================================================================

export function useVisitors(filters: VisitorFilters = {}) {
  return useQuery({
    queryKey: vmKeys.visitorList(filters),
    queryFn: () => getVisitors(filters),
    placeholderData: (prev) => prev,
  });
}

export function useVisitorDetail(publicId: string) {
  return useQuery({
    queryKey: vmKeys.visitorDetail(publicId),
    queryFn: () => getVisitorById(publicId),
    enabled: !!publicId,
  });
}

export function useVisitorSearch(query: string) {
  return useQuery({
    queryKey: vmKeys.visitorSearch(query),
    queryFn: () => searchVisitors(query),
    enabled: query.length >= 2,
  });
}

export function useVisitorHistory(publicId: string) {
  return useQuery({
    queryKey: vmKeys.visitorHistory(publicId),
    queryFn: () => getVisitorHistory(publicId),
    enabled: !!publicId,
  });
}

export function useCreateVisitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VisitorCreate) => createVisitor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.visitors() });
    },
  });
}

// =============================================================================
// Visit Hooks
// =============================================================================

export function useVisits(filters: VisitFilters = {}) {
  return useQuery({
    queryKey: vmKeys.visitList(filters),
    queryFn: () => getVisits(filters),
    placeholderData: (prev) => prev,
  });
}

export function useActiveVisits(school?: number) {
  return useQuery({
    queryKey: vmKeys.activeVisits(school),
    queryFn: () => getActiveVisits(school),
    refetchInterval: 30_000,
  });
}

// school is required by the endpoint: "today" is the site's local day.
export function useTodayVisits(school?: number) {
  return useQuery({
    queryKey: vmKeys.todayVisits(school),
    queryFn: () => getTodayVisits(school as number),
    enabled: school != null,
  });
}

// =============================================================================
// Check-In / Check-Out Mutations
// =============================================================================

export function useCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CheckInRequest) => checkIn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.visits() });
      queryClient.invalidateQueries({ queryKey: vmKeys.visitors() });
    },
  });
}

export function useCheckOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CheckOutRequest) => checkOut(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.visits() });
    },
  });
}

// =============================================================================
// Watch List Hooks
// =============================================================================

export function useWatchListEntries(filters: WatchListFilters = {}) {
  return useQuery({
    queryKey: vmKeys.watchListEntries(filters),
    queryFn: () => getWatchListEntries(filters),
    placeholderData: (prev) => prev,
  });
}

export function useCreateWatchListEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WatchListEntryWrite | FormData) =>
      createWatchListEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.watchList() });
    },
  });
}

export function useUpdateWatchListEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: Partial<WatchListEntryWrite> | FormData;
    }) => updateWatchListEntry(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.watchList() });
    },
  });
}

export function useDeleteWatchListEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => deleteWatchListEntry(publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.watchList() });
    },
  });
}

// =============================================================================
// Reason Type Hooks
// =============================================================================

export function useReasonTypes() {
  return useQuery({
    queryKey: vmKeys.reasonTypes(),
    queryFn: getReasonTypes,
  });
}

export function useCreateReasonType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VisitReasonTypeWrite) => createReasonType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.reasonTypes() });
    },
  });
}

export function useUpdateReasonType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: Partial<VisitReasonTypeWrite>;
    }) => updateReasonType(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.reasonTypes() });
    },
  });
}

export function useDeleteReasonType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => deleteReasonType(publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.reasonTypes() });
    },
  });
}
export function useReasonTypeById(id: string, enabled = true) {
  return useQuery({
    queryKey: vmKeys.reasonTypeById(id),
    queryFn: () => getReasonTypeById(id),
    enabled: enabled && !!id,
  });
}
// =============================================================================
// Category Type Hooks
// =============================================================================

export function useCategoryTypes() {
  return useQuery({
    queryKey: vmKeys.categoryTypes(),
    queryFn: getCategoryTypes,
  });
}

export function useCreateCategoryType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VisitorCategoryTypeWrite) => createCategoryType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.categoryTypes() });
    },
  });
}

export function useUpdateCategoryType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: Partial<VisitorCategoryTypeWrite>;
    }) => updateCategoryType(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.categoryTypes() });
    },
  });
}

export function useDeleteCategoryType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => deleteCategoryType(publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.categoryTypes() });
    },
  });
}
export function useCategoryTypeById(id: string, enabled = true) {
  return useQuery({
    queryKey: vmKeys.categoryTypeById(id),
    queryFn: () => getCategoryTypeById(id),
    enabled: enabled && !!id,
  });
}

// =============================================================================
// Badge Template Hooks
// =============================================================================

export function useBadgeTemplates() {
  return useQuery({
    queryKey: vmKeys.badgeTemplates(),
    queryFn: getBadgeTemplates,
  });
}

export function useCreateBadgeTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BadgeTemplateWrite) => createBadgeTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.badgeTemplates() });
    },
  });
}

export function useUpdateBadgeTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: Partial<BadgeTemplateWrite>;
    }) => updateBadgeTemplate(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.badgeTemplates() });
    },
  });
}

export function useDeleteBadgeTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => deleteBadgeTemplate(publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.badgeTemplates() });
    },
  });
}
export function useBadgeTemplateById(id: string, enabled = true) {
  return useQuery({
    queryKey: vmKeys.badgeTemplateById(id),
    queryFn: () => getBadgeTemplateById(id),
    enabled: enabled && !!id,
  });
}

// =============================================================================
// Screening Configuration Hooks
// =============================================================================

export function useScreeningConfig() {
  return useQuery({
    queryKey: vmKeys.screeningConfig(),
    queryFn: getScreeningConfig,
  });
}

export function useCreateScreeningConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ScreeningConfigurationWrite) =>
      createScreeningConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.screeningConfig() });
    },
  });
}

export function useUpdateScreeningConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: ScreeningConfigurationWrite;
    }) => updateScreeningConfig(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.screeningConfig() });
    },
  });
}

// =============================================================================
// Employee Reason Type Hooks (ADR-073 Phase 2)
// =============================================================================

export function useEmployeeReasonTypes() {
  return useQuery({
    queryKey: vmKeys.employeeReasonTypes(),
    queryFn: getEmployeeReasonTypes,
  });
}

export function useEmployeeReasonTypeById(id: string, enabled = true) {
  return useQuery({
    queryKey: vmKeys.employeeReasonTypeById(id),
    queryFn: () => getEmployeeReasonTypeById(id),
    enabled: enabled && !!id,
  });
}

export function useCreateEmployeeReasonType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EmployeeReasonTypeWrite) => createEmployeeReasonType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.employeeReasonTypes() });
    },
  });
}

export function useUpdateEmployeeReasonType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: Partial<EmployeeReasonTypeWrite>;
    }) => updateEmployeeReasonType(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.employeeReasonTypes() });
    },
  });
}

export function useDeleteEmployeeReasonType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => deleteEmployeeReasonType(publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.employeeReasonTypes() });
    },
  });
}

// =============================================================================
// Employee Visit Hooks (ADR-073 Phase 2)
// =============================================================================

export function useEmployeeVisits(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: vmKeys.employeeVisitList(filters),
    queryFn: () => getEmployeeVisits(filters),
    placeholderData: (prev) => prev,
  });
}

export function useActiveEmployeeVisits(schoolId?: number) {
  return useQuery({
    queryKey: vmKeys.activeEmployeeVisits(schoolId),
    queryFn: () => getActiveEmployeeVisits(schoolId),
  });
}

// school is required by the endpoint, as for useTodayVisits.
export function useTodayEmployeeVisits(schoolId?: number) {
  return useQuery({
    queryKey: vmKeys.todayEmployeeVisits(schoolId),
    queryFn: () => getTodayEmployeeVisits(schoolId as number),
    enabled: schoolId != null,
  });
}

export function useEmployeeVisitDetail(publicId: string) {
  return useQuery({
    queryKey: vmKeys.employeeVisitDetail(publicId),
    queryFn: () => getEmployeeVisitById(publicId),
    enabled: !!publicId,
  });
}

export function useCreateEmployeeVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EmployeeVisitCreate) => createEmployeeVisit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.employeeVisits() });
    },
  });
}

export function useUpdateEmployeeVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: Partial<EmployeeVisitUpdate>;
    }) => updateEmployeeVisit(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.employeeVisits() });
    },
  });
}

export function useDeleteEmployeeVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => deleteEmployeeVisit(publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.employeeVisits() });
    },
  });
}

// =============================================================================
// Car Rider Queue Hooks (ADR-073 Phase 2)
// =============================================================================

export function useCarRiderQueue() {
  return useQuery({
    queryKey: vmKeys.carRiderQueue(),
    queryFn: () => getCarRiderQueue(),
  });
}

export function useCarRiderQueueEntryById(publicId: string, enabled = true) {
  return useQuery({
    queryKey: vmKeys.carRiderQueueEntryById(publicId),
    queryFn: () => getCarRiderQueueEntryById(publicId),
    enabled: enabled && !!publicId,
  });
}

export function useCreateCarRiderQueueEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CarRiderQueueEntryWrite) => createCarRiderQueueEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.carRiderQueue() });
    },
  });
}

export function useUpdateCarRiderQueueEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: Partial<CarRiderQueueEntryWrite>;
    }) => updateCarRiderQueueEntry(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.carRiderQueue() });
    },
  });
}

export function useDeleteCarRiderQueueEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => deleteCarRiderQueueEntry(publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.carRiderQueue() });
    },
  });
}

// =============================================================================
// Pickup Delegation Hooks (ADR-073 Phase 2)
// =============================================================================

export function usePickupDelegations() {
  return useQuery({
    queryKey: vmKeys.pickupDelegations(),
    queryFn: () => getPickupDelegations(),
  });
}

export function usePickupDelegationById(publicId: string, enabled = true) {
  return useQuery({
    queryKey: vmKeys.pickupDelegationById(publicId),
    queryFn: () => getPickupDelegationById(publicId),
    enabled: enabled && !!publicId,
  });
}

export function useCreatePickupDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PickupDelegationWrite) => createPickupDelegation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.pickupDelegations() });
    },
  });
}

export function useUpdatePickupDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      data,
    }: {
      publicId: string;
      data: Partial<PickupDelegationWrite>;
    }) => updatePickupDelegation(publicId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.pickupDelegations() });
    },
  });
}

export function useDeletePickupDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => deletePickupDelegation(publicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vmKeys.pickupDelegations() });
    },
  });
}
